import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { DictionaryGapLogListOptions } from "@shared-core/data/adapter";
import type { DictionaryGapLogEntry } from "@shared-core/data/api-types";
import {
  formatDateOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import { staffLineTitle } from "@shared-core/domain/bespoke-title";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { ArrowUpDown, ClipboardList, Search } from "lucide-react";
import { SettingsBreadcrumbs } from "../app/components/patterns/SettingsBreadcrumbs";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";

const PAGE_SIZE = 25;

// The dictionary gap log (v1 completion E): every time staff used work the
// dictionary could not describe, so the people who extend the dictionary can
// see what is missing. Read-only; entries are written when a supplier-quoted
// line first appears on a quote or an order. Filter, sort, search and paging
// all run on the server (docs/list-endpoints.md).

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "-logged_at", label: "Newest first" },
  { value: "logged_at", label: "Oldest first" },
  { value: "-quantity", label: "Largest quantity first" },
  { value: "-cost_pence", label: "Highest cost first" },
];

/** "ORD-K51207.01" is a line of "ORD-K51207"; a quote's line likewise. */
function parentNumber(sourceRef: string): string {
  return sourceRef.replace(/\.\d{2,}$/, "");
}

function SourceLink({ entry }: { entry: DictionaryGapLogEntry }) {
  const parent = parentNumber(entry.source_ref);
  const to =
    entry.source_kind === "quote_line"
      ? paths.quoteDetail({ quoteNo: parent })
      : paths.orderDetail({ orderNo: parent });
  return (
    <Link to={to} className="font-medium underline underline-offset-2">
      {entry.source_ref}
    </Link>
  );
}

function madeBy(entry: DictionaryGapLogEntry): string {
  return entry.route === "make"
    ? "Us (Tharstern)"
    : (entry.supplier_name ?? "A supplier");
}

export function DictionaryGapLogPage() {
  const adapter = useDataAdapter();
  const [searchParams, setSearchParams] = useSearchParams();

  const sort = searchParams.get("sort") ?? "-logged_at";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const route = (searchParams.get("route") ?? "") as "buy" | "make" | "";
  const loggedFrom = searchParams.get("from") ?? "";
  const loggedTo = searchParams.get("to") ?? "";

  const [entries, setEntries] = useState<DictionaryGapLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null) params.delete(key);
            else params.set(key, value);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const load = useCallback(() => {
    const options: DictionaryGapLogListOptions = {
      page,
      pageSize: PAGE_SIZE,
      sort,
    };
    if (debouncedQ) options.q = debouncedQ;
    if (route) options.route = route;
    if (loggedFrom) options.loggedFrom = loggedFrom;
    if (loggedTo) options.loggedTo = loggedTo;
    setLoadError(false);
    adapter
      .listDictionaryGapLog(options)
      .then((result) => {
        setEntries(result.items);
        setTotal(result.total);
        setTotalPages(result.totalPages);
        setFirstLoad(false);
      })
      .catch(() => {
        setLoadError(true);
        setFirstLoad(false);
      });
  }, [adapter, page, sort, debouncedQ, route, loggedFrom, loggedTo]);

  useEffect(() => {
    load();
  }, [load]);

  const hasFilters = Boolean(q || route || loggedFrom || loggedTo);

  return (
    <div className="space-y-6">
      <SettingsBreadcrumbs
        crumbs={[
          { label: "Settings", to: paths.settingsIntegrations() },
          { label: "Catalogue", to: paths.settingsCatalogues() },
          { label: "Dictionary", to: paths.dictionaryAdmin() },
          { label: "Gap log" },
        ]}
      />
      <div>
        <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
          <ClipboardList className="h-6 w-6" /> Dictionary gap log
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Work the dictionary couldn&rsquo;t describe, as staff recorded it.
          Each row is one use.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative max-w-sm flex-1 min-w-[12rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            aria-label="Search the gap log"
            placeholder="Title, description, reason or supplier"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              updateParams({ page: null });
            }}
            className="pl-9"
          />
        </div>
        <select
          aria-label="Who made it"
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={route}
          onChange={(e) =>
            updateParams({ route: e.target.value || null, page: null })
          }
        >
          <option value="">Anyone</option>
          <option value="buy">A supplier</option>
          <option value="make">Us (Tharstern)</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          From
          <input
            type="date"
            aria-label="Logged from"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
            value={loggedFrom}
            onChange={(e) =>
              updateParams({ from: e.target.value || null, page: null })
            }
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          To
          <input
            type="date"
            aria-label="Logged to"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
            value={loggedTo}
            onChange={(e) =>
              updateParams({ to: e.target.value || null, page: null })
            }
          />
        </label>
        <div className="flex items-center gap-2">
          <ArrowUpDown className="size-4 text-muted-foreground" />
          <select
            aria-label="Sort"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={sort}
            onChange={(e) =>
              updateParams({
                sort: e.target.value === "-logged_at" ? null : e.target.value,
                page: null,
              })
            }
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Logged</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Why it isn&rsquo;t in the dictionary</TableHead>
              <TableHead>Who made it</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead>Where</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {firstLoad ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : loadError ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2 text-destructive">
                    <p>Couldn&rsquo;t load the gap log.</p>
                    <Button variant="outline" size="sm" onClick={load}>
                      Try again
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  {hasFilters
                    ? "No uses match these filters"
                    : "Nothing logged yet. A use appears here when a supplier-quoted line is first put on a quote or an order."}
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="whitespace-nowrap">
                    {formatDateOrDash(entry.logged_at)}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">
                      {staffLineTitle(entry.title, true)}
                    </div>
                    {entry.description && (
                      <div className="text-xs text-muted-foreground">
                        {entry.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{entry.reason}</TableCell>
                  <TableCell>{madeBy(entry)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {entry.quantity.toLocaleString("en-GB")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatQuotedPrice(entry.cost_pence)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <SourceLink entry={entry} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => updateParams({ page: p === 1 ? null : String(p) })}
      />

      {!firstLoad && !loadError && (
        <p className="text-xs text-muted-foreground">
          {total} {total === 1 ? "use" : "uses"}
        </p>
      )}
    </div>
  );
}
